import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { UserPlus, Mail, Trash2, RotateCw, Users, Shield, User, Edit2, KeyRound, ArrowRightLeft, Crown, CreditCard, Send, Clock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ClearStaleUserInvitationsResponse,
  CreateUserInvitationResponse,
  GetAuthUserResponse,
  GetBatchUserDeletionPreviewResponse,
  GetUserDeletionPreviewResponse,
  ListAdminTenantsResponse,
  ListResellerPlansResponse,
  ListUserInvitationsResponse,
  ListUsersResponse,
  MoveUserToTenantResponse,
  ResendUserInvitationResponse,
  ResetUserPasswordResponse,
  UpdateTenantPlanResponse,
} from "@workspace/api-zod";
import type {
  AdminTenant,
  BatchUserDeletionPreview,
  CreateUserInvitationRequest,
  ResellerPlan,
  UpdateUserInvitationRequest,
  UserDeletionPreview,
  UserInvitation,
  UserWithTenant,
} from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";

// Invitation payload comes from the generated OpenAPI request type so it
// can't drift from the documented POST/PATCH /user-invitations contract.

// Format an ISO timestamp as a relative "X days ago" string (matches bulk-delete dialog).
function formatDaysAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
}

// Row shape returned by GET /api/users comes from the generated,
// zod-validated UserWithTenant type (@workspace/api-client-react).
type UserRow = UserWithTenant;

// Validated (not merely asserted) shape of the /api/auth/user response.
// Only the fields this page consumes are declared; unknown extra fields pass through.
export default function UserManagement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editInviteDialogOpen, setEditInviteDialogOpen] = useState(false);
  const [editingInvitation, setEditingInvitation] = useState<UserInvitation | null>(null);
  const [moveUserDialogOpen, setMoveUserDialogOpen] = useState(false);
  const [userToMove, setUserToMove] = useState<UserRow | null>(null);
  const [targetTenantId, setTargetTenantId] = useState<string>("");
  const [viewingTenantId, setViewingTenantId] = useState<string | undefined>(undefined);
  const [changeRoleDialogOpen, setChangeRoleDialogOpen] = useState(false);
  const [userToChangeRole, setUserToChangeRole] = useState<UserRow | null>(null);
  const [newRole, setNewRole] = useState<"user" | "admin">("user");
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [tenantToChangePlan, setTenantToChangePlan] = useState<{ id: string; name: string; currentPlanId?: string | null } | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [deleteDialogUser, setDeleteDialogUser] = useState<UserRow | null>(null);
  const [deletionPreview, setDeletionPreview] = useState<UserDeletionPreview | null>(null);
  const [deletionPreviewLoading, setDeletionPreviewLoading] = useState(false);
  const [revokeDialogInvitation, setRevokeDialogInvitation] = useState<UserInvitation | null>(null);
  const [showAllInvitations, setShowAllInvitations] = useState(false);
  const [clearStaleDialogOpen, setClearStaleDialogOpen] = useState(false);
  const [revokeDialogPreview, setRevokeDialogPreview] = useState<UserDeletionPreview | null>(null);
  const [revokeDialogPreviewLoading, setRevokeDialogPreviewLoading] = useState(false);
  const [revokeDialogPreviewError, setRevokeDialogPreviewError] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeletionPreview, setBulkDeletionPreview] = useState<BatchUserDeletionPreview | null>(null);
  const [bulkDeletionPreviewLoading, setBulkDeletionPreviewLoading] = useState(false);
  const [inviteData, setInviteData] = useState<InviteUserData>({
    email: "",
    firstName: "",
    lastName: "",
    role: "user",
    invitationType: "member", // Default to member invitation
    preferredTenantName: "",
  });
  const [editData, setEditData] = useState<UpdateUserInvitationRequest>({
    email: "",
    firstName: "",
    lastName: "",
    role: "user",
  });

  // Fetch current user to check if master admin
  const { data: currentUser } = useQuery({
    queryKey: ["/api/auth/user"],
    select: (data: unknown) => GetAuthUserResponse.parse(data).user,
  });

  // Use the isMasterAdmin flag from the backend instead of hard-coding email
  const isMasterAdmin = currentUser?.isMasterAdmin === true;

  // Fetch all tenants (only for master admin) — validated against the generated schema
  const { data: allTenants } = useQuery({
    queryKey: ["/api/admin/tenants"],
    enabled: isMasterAdmin,
    select: (data: unknown): AdminTenant[] => ListAdminTenantsResponse.parse(data),
  });

  // Fetch available reseller plans (master admin only, for the change-plan dropdown)
  const { data: resellerPlans } = useQuery({
    queryKey: ["/api/reseller/plans"],
    enabled: isMasterAdmin,
    select: (data: unknown): ResellerPlan[] => ListResellerPlansResponse.parse(data),
  });

  // Fetch existing users
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: viewingTenantId 
      ? ["/api/users", { tenantId: viewingTenantId }]
      : ["/api/users"],
    queryFn: async () => {
      const url = viewingTenantId 
        ? `/api/users?tenantId=${viewingTenantId}`
        : '/api/users';
      const response = await fetch(url, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      return response.json();
    },
    select: (data: unknown): UserRow[] => ListUsersResponse.parse(data),
  });

  // Fetch pending invitations
  const { data: invitations, isLoading: invitationsLoading } = useQuery({
    queryKey: viewingTenantId 
      ? ["/api/user-invitations", { tenantId: viewingTenantId }]
      : ["/api/user-invitations"],
    queryFn: async () => {
      const url = viewingTenantId 
        ? `/api/user-invitations?tenantId=${viewingTenantId}`
        : '/api/user-invitations';
      const response = await fetch(url, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch invitations');
      return response.json();
    },
    select: (data: unknown): UserInvitation[] => ListUserInvitationsResponse.parse(data),
  });

  const activeInvitations = (invitations || []).filter(
    (inv) => inv.status === "pending" || inv.status === "accepted"
  );
  const staleInvitations = (invitations || []).filter(
    (inv) => inv.status === "expired" || inv.status === "cancelled"
  );
  const visibleInvitations = showAllInvitations
    ? [...activeInvitations, ...staleInvitations]
    : activeInvitations;

  // Invite user mutation
  const inviteUserMutation = useMutation({
    mutationFn: (data: InviteUserData) => 
      apiRequest("POST", "/api/user-invitations", data),
    onSuccess: async (res) => {
      const created = await res
        .json()
        .then((json) => CreateUserInvitationResponse.parse(json))
        .catch(() => null);
      if (created && created.emailSent === false) {
        toast({
          title: "Invitation created, but email failed",
          description: "The invitation email could not be delivered. Use Resend to try sending it again.",
          variant: "destructive",
        });
      }
      // Invalidate all user-invitations queries (including tenant-specific ones)
      queryClient.invalidateQueries({ queryKey: ["/api/user-invitations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setInviteDialogOpen(false);
      setInviteData({ 
        email: "", 
        firstName: "", 
        lastName: "", 
        role: "user", 
        tenantId: undefined,
        invitationType: "member", // Reset to default member invitation
        preferredTenantName: "", // Clear family name
      });
    },
    onError: (error: any) => {
      // Error handled silently
    },
  });

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/user-invitations/${id}/resend`),
    onSuccess: async (res) => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-invitations"] });
      const result = await res
        .json()
        .then((json) => ResendUserInvitationResponse.parse(json))
        .catch(() => null);
      if (result && result.emailSent === false) {
        toast({
          title: "Email could not be delivered",
          description: "The invitation was updated, but the email failed to send. Please try resending again.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Invitation resent", description: "The invitation email has been resent successfully." });
      }
    },
    onError: () => {
      toast({ title: "Failed to resend", description: "Could not resend the invitation. Please try again.", variant: "destructive" });
    },
  });

  // Edit invitation mutation
  const editInvitationMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserInvitationRequest }) =>
      apiRequest("PATCH", `/api/user-invitations/${id}`, data),
    onSuccess: () => {
      // Invalidate all user-invitations queries (including tenant-specific ones)
      queryClient.invalidateQueries({ queryKey: ["/api/user-invitations"] });
      setEditInviteDialogOpen(false);
      setEditingInvitation(null);
    },
    onError: (error: any) => {
      // Error handled silently
    },
  });

  // Delete invitation mutation
  const deleteInvitationMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      apiRequest("DELETE", `/api/user-invitations/${id}${force ? "?force=true" : ""}`),
    onSuccess: () => {
      // Invalidate all user-invitations queries (including tenant-specific ones)
      queryClient.invalidateQueries({ queryKey: ["/api/user-invitations"] });
      setRevokeDialogInvitation(null);
      setRevokeDialogPreview(null);
      setRevokeDialogPreviewError(false);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to cancel invitation",
        description: error?.message || "An error occurred while cancelling the invitation. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Bulk clear expired & cancelled invitations mutation
  const clearStaleInvitationsMutation = useMutation({
    mutationFn: async () => {
      const url = viewingTenantId
        ? `/api/user-invitations/stale?tenantId=${viewingTenantId}`
        : "/api/user-invitations/stale";
      const res = await apiRequest("DELETE", url);
      return ClearStaleUserInvitationsResponse.parse(await res.json());
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-invitations"] });
      setClearStaleDialogOpen(false);
      toast({
        title: "Invitations cleared",
        description: `Removed ${data.deletedCount} expired/cancelled invitation${data.deletedCount === 1 ? "" : "s"}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to clear invitations",
        description: error?.message || "An error occurred while clearing invitations. Please try again.",
        variant: "destructive",
      });
    },
  });

  async function openRevokeInvitationDialog(invitation: UserInvitation) {
    setRevokeDialogInvitation(invitation);
    setRevokeDialogPreview(null);
    setRevokeDialogPreviewError(false);
    if (invitation.status === "accepted" && invitation.email) {
      setRevokeDialogPreviewLoading(true);
      try {
        const matchedUser = users?.find((u) => u.email === invitation.email);
        if (matchedUser?.id) {
          const previewRes = await apiRequest("GET", `/api/users/${matchedUser.id}/deletion-preview`);
          if (previewRes.ok) {
            setRevokeDialogPreview(GetUserDeletionPreviewResponse.parse(await previewRes.json()));
          } else {
            setRevokeDialogPreviewError(true);
          }
        }
      } catch {
        setRevokeDialogPreviewError(true);
      } finally {
        setRevokeDialogPreviewLoading(false);
      }
    }
  }

  // Delete user mutation
  const deleteUserPendingRef = useRef(false);
  const deleteUserMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setDeleteDialogUser(null);
      setDeletionPreview(null);
      toast({
        title: "User removed",
        description: "The user has been successfully removed from your organization.",
      });
    },
    onError: (error: any) => {
      console.error("Error deleting user:", error);
      toast({
        title: "Failed to remove user",
        description: error?.message || "An error occurred while removing the user. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      deleteUserPendingRef.current = false;
    },
  });

  async function openDeleteDialog(user: UserRow) {
    setDeleteDialogUser(user);
    setDeletionPreview(null);
    setDeletionPreviewLoading(true);
    try {
      const res = await apiRequest("GET", `/api/users/${user.id}/deletion-preview`);
      setDeletionPreview(GetUserDeletionPreviewResponse.parse(await res.json()));
    } catch {
      // Preview failed — show dialog without impact details
    } finally {
      setDeletionPreviewLoading(false);
    }
  }

  const bulkDeletePendingRef = useRef(false);
  const bulkDeleteUserMutation = useMutation({
    onSettled: () => {
      bulkDeletePendingRef.current = false;
    },
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => apiRequest("DELETE", `/api/users/${id}`))
      );
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        throw Object.assign(new Error("partial"), { succeededCount: ids.length - failed.length, failedCount: failed.length });
      }
    },
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      const count = ids.length;
      setSelectedUserIds(new Set());
      setBulkDeleteDialogOpen(false);
      setBulkDeletionPreview(null);
      toast({
        title: `${count} user${count === 1 ? "" : "s"} removed`,
        description: `Successfully removed ${count} user${count === 1 ? "" : "s"} from your organization.`,
      });
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      if (error?.message === "partial") {
        const { succeededCount, failedCount } = error;
        setSelectedUserIds(new Set());
        setBulkDeleteDialogOpen(false);
        setBulkDeletionPreview(null);
        toast({
          title: "Partial removal",
          description: `${succeededCount} user${succeededCount === 1 ? "" : "s"} removed; ${failedCount} could not be deleted. Please try again for the remaining users.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Failed to remove users",
          description: error?.message || "An error occurred while removing users. Please try again.",
          variant: "destructive",
        });
      }
    },
  });

  async function openBulkDeleteDialog(ids: Set<string>) {
    setBulkDeleteDialogOpen(true);
    setBulkDeletionPreview(null);
    setBulkDeletionPreviewLoading(true);
    try {
      const res = await apiRequest("POST", "/api/users/deletion-preview-batch", { userIds: Array.from(ids) });
      setBulkDeletionPreview(GetBatchUserDeletionPreviewResponse.parse(await res.json()));
    } catch {
      // Preview failed — show dialog without impact details
    } finally {
      setBulkDeletionPreviewLoading(false);
    }
  }

  // Move user mutation — response validated against the generated schema
  const moveUserMutation = useMutation({
    mutationFn: async (data: { userId: string; targetTenantId: string }) => {
      const res = await apiRequest("POST", "/api/admin/move-user", data);
      return MoveUserToTenantResponse.parse(await res.json());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setMoveUserDialogOpen(false);
      setUserToMove(null);
      setTargetTenantId("");
    },
    onError: (error: any) => {
      // Error handled silently
    },
  });

  // Reset password mutation — response validated against the generated schema
  const resetPasswordMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/users/${id}/reset-password`);
      return ResetUserPasswordResponse.parse(await res.json());
    },
    onSuccess: (data) => {
      toast({
        title: "Password reset",
        description: data.message || "A temporary password has been sent to the user's email address.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Password reset failed",
        description: error?.message || "Could not reset the password. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Change role mutation
  const changeRolePendingRef = useRef(false);
  const changeRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: "user" | "admin" }) =>
      apiRequest("PATCH", `/api/users/${userId}`, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setChangeRoleDialogOpen(false);
      setUserToChangeRole(null);
      toast({
        title: "Role updated",
        description: "The user's role has been successfully updated.",
      });
    },
    onError: (error: any) => {
      console.error("Error changing user role:", error);
      toast({
        title: "Failed to update role",
        description: error?.message || "An error occurred while updating the user role. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      changeRolePendingRef.current = false;
    },
  });

  // Grant/revoke master admin mutation (master admin only)
  const masterAdminMutation = useMutation({
    mutationFn: ({ userId, grant }: { userId: string; grant: boolean }) =>
      apiRequest("PATCH", `/api/users/${userId}`, { isMasterAdmin: grant }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      toast({
        title: vars.grant ? "Master admin granted" : "Master admin removed",
        description: vars.grant
          ? "This user now has full master admin access."
          : "This user no longer has master admin access.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update master admin status",
        description: error?.message || "An error occurred. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Change tenant plan mutation (master admin only) — response validated against the generated schema
  const changePlanMutation = useMutation({
    mutationFn: async ({ tenantId, planId }: { tenantId: string; planId: string }) => {
      const res = await apiRequest("PATCH", `/api/admin/tenants/${tenantId}/plan`, { planId });
      return UpdateTenantPlanResponse.parse(await res.json());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tenants"] });
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setChangePlanDialogOpen(false);
      setTenantToChangePlan(null);
      setSelectedPlanId("");
      toast({
        title: "Plan updated",
        description: "The tenant's plan has been successfully changed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update plan",
        description: error?.message || "An error occurred while updating the plan.",
        variant: "destructive",
      });
    },
  });

  // Helper: get plan display name for a tenant
  const getPlanDisplayName = (tenantId: string): string => {
    const tenant = allTenants?.find(t => t.id === tenantId);
    if (!tenant) return "Unknown";
    if (tenant.currentPlanId && resellerPlans) {
      const plan = resellerPlans.find(p => p.id === tenant.currentPlanId);
      if (plan) return plan.name;
    }
    return tenant.planType || "Free";
  };

  const handleInviteUser = () => {
    if (!inviteData.email) {
      return;
    }
    inviteUserMutation.mutate(inviteData);
  };

  const handleEditInvitation = (invitation: UserInvitation) => {
    setEditingInvitation(invitation);
    setEditData({
      email: invitation.email,
      firstName: invitation.firstName || "",
      lastName: invitation.lastName || "",
      role: invitation.role === "admin" ? "admin" : "user",
    });
    setEditInviteDialogOpen(true);
  };

  const handleUpdateInvitation = () => {
    if (!editingInvitation || !editData.email) {
      return;
    }
    editInvitationMutation.mutate({ id: editingInvitation.id, data: editData });
  };

  // Re-render every minute so the "Expires in" countdown and urgent highlight stay fresh
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Also fire exactly at the nearest pending invitation's expiry so the row flips
  // to Expired at the boundary instead of waiting for the next minute tick.
  useEffect(() => {
    const pendingExpiries = (invitations || [])
      .filter((inv) => inv.status === "pending" && inv.expiresAt)
      .map((inv) => new Date(inv.expiresAt as string | Date).getTime())
      .filter((t) => !isNaN(t) && t > now);
    if (pendingExpiries.length === 0) return;
    const nearest = Math.min(...pendingExpiries);
    // +50ms so Date.now() is guaranteed past the boundary when the callback runs
    const timeout = setTimeout(() => setNow(Date.now()), Math.max(0, nearest - now) + 50);
    return () => clearTimeout(timeout);
  }, [invitations, now]);

  const getExpiryInfo = (expiresAt: string | Date | null | undefined) => {
    if (!expiresAt) return null;
    const expiry = new Date(expiresAt);
    if (isNaN(expiry.getTime())) return null;
    const msLeft = expiry.getTime() - now;
    if (msLeft <= 0) return null;
    const hoursLeft = msLeft / (1000 * 60 * 60);
    let label: string;
    if (hoursLeft < 1) {
      const minutes = Math.max(1, Math.floor(msLeft / (1000 * 60)));
      label = `Expires in ${minutes} minute${minutes === 1 ? "" : "s"}`;
    } else if (hoursLeft < 24) {
      const hours = Math.floor(hoursLeft);
      label = `Expires in ${hours} hour${hours === 1 ? "" : "s"}`;
    } else {
      const days = Math.floor(hoursLeft / 24);
      label = `Expires in ${days} day${days === 1 ? "" : "s"}`;
    }
    return { label, urgent: hoursLeft < 24 };
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" data-testid={`status-${status}`}>Pending</Badge>;
      case "accepted":
        return <Badge variant="default" data-testid={`status-${status}`}>Accepted</Badge>;
      case "expired":
        return <Badge variant="destructive" data-testid={`status-${status}`}>Expired</Badge>;
      case "cancelled":
        return <Badge variant="outline" data-testid={`status-${status}`}>Cancelled</Badge>;
      default:
        return <Badge variant="secondary" data-testid={`status-${status}`}>{status}</Badge>;
    }
  };

  const getRoleBadge = (role: string, isMasterAdmin?: boolean) => {
    if (role === "admin") {
      return (
        <div className="flex items-center gap-2">
          <Badge variant="default" className="bg-purple-100 text-purple-800" data-testid={`role-${role}`}>
            <Shield className="w-3 h-3 mr-1" />
            Admin
          </Badge>
          {isMasterAdmin && (
            <Badge variant="default" className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white" data-testid="badge-master-admin">
              <Crown className="w-3 h-3 mr-1" />
              Master Admin
            </Badge>
          )}
        </div>
      );
    }
    
    return (
      <Badge variant="outline" data-testid={`role-${role}`}>
        <User className="w-3 h-3 mr-1" />
        User
      </Badge>
    );
  };

  return (
    <div className="p-4 md:p-6 w-full max-w-full space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-testid="page-title">User Management</h1>
          <p className="text-muted-foreground">
            Manage team members and invitations for your organization
          </p>
        </div>
        <Dialog open={inviteDialogOpen} onOpenChange={(open) => {
          if (open && viewingTenantId) {
            // Pre-fill the tenant when viewing a specific tenant
            setInviteData(prev => ({ ...prev, tenantId: viewingTenantId }));
          }
          setInviteDialogOpen(open);
        }}>
          <DialogTrigger asChild>
            <Button data-testid="button-invite-user">
              <UserPlus className="w-4 h-4 mr-2" />
              Invite User
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="dialog-invite-user" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Invite New User</DialogTitle>
              <DialogDescription>
                Send an invitation to add a new team member to your organization.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  data-testid="input-email"
                  type="email"
                  placeholder="user@example.com"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    data-testid="input-first-name"
                    placeholder="John"
                    value={inviteData.firstName}
                    onChange={(e) => setInviteData({ ...inviteData, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    data-testid="input-last-name"
                    placeholder="Doe"
                    value={inviteData.lastName}
                    onChange={(e) => setInviteData({ ...inviteData, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select 
                  value={inviteData.role} 
                  onValueChange={(value) => setInviteData({ ...inviteData, role: value as InviteUserData["role"] })}
                >
                  <SelectTrigger data-testid="select-role">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user" data-testid="role-option-user">User</SelectItem>
                    <SelectItem value="admin" data-testid="role-option-admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="invitationType">Invitation Type</Label>
                <Select 
                  value={inviteData.invitationType} 
                  onValueChange={(value) => setInviteData({ ...inviteData, invitationType: value as InviteUserData["invitationType"] })}
                >
                  <SelectTrigger data-testid="select-invitation-type">
                    <SelectValue placeholder="Select invitation type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member" data-testid="invitation-type-member">Member (Join this organization)</SelectItem>
                    <SelectItem value="reseller" data-testid="invitation-type-reseller">Reseller (Create new family/organization)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground mt-1">
                  {inviteData.invitationType === 'reseller' 
                    ? 'Invited user will get their own organization/family' 
                    : 'Invited user will join your current organization'}
                </p>
              </div>
              {inviteData.invitationType === 'reseller' && (
                <div>
                  <Label htmlFor="preferredTenantName">Family Name (Optional)</Label>
                  <Input
                    id="preferredTenantName"
                    data-testid="input-preferred-tenant-name"
                    placeholder="e.g., Smith Family, Jones Household..."
                    value={inviteData.preferredTenantName}
                    onChange={(e) => setInviteData({ ...inviteData, preferredTenantName: e.target.value })}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Leave empty to use the invitee's name as the family name
                  </p>
                </div>
              )}
              {isMasterAdmin && allTenants && allTenants.length > 0 && (
                <div>
                  <Label htmlFor="tenant">Tenant (Organization)</Label>
                  <Select 
                    value={inviteData.tenantId} 
                    onValueChange={(value) => setInviteData({ ...inviteData, tenantId: value })}
                  >
                    <SelectTrigger data-testid="select-tenant">
                      <SelectValue placeholder="Select a tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {allTenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id} data-testid={`tenant-option-${tenant.id}`}>
                          {tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground mt-1">
                    Leave empty to add user to your current organization
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setInviteDialogOpen(false)}
                data-testid="button-cancel-invite"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleInviteUser} 
                disabled={inviteUserMutation.isPending}
                data-testid="button-send-invite"
              >
                {inviteUserMutation.isPending ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={editInviteDialogOpen} onOpenChange={setEditInviteDialogOpen}>
          <DialogContent data-testid="dialog-edit-invitation" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Invitation</DialogTitle>
              <DialogDescription>
                Update the invitation details for this team member.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-email">Email Address *</Label>
                <Input
                  id="edit-email"
                  data-testid="input-edit-email"
                  type="email"
                  placeholder="user@example.com"
                  value={editData.email}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-firstName">First Name</Label>
                  <Input
                    id="edit-firstName"
                    data-testid="input-edit-first-name"
                    placeholder="John"
                    value={editData.firstName}
                    onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-lastName">Last Name</Label>
                  <Input
                    id="edit-lastName"
                    data-testid="input-edit-last-name"
                    placeholder="Doe"
                    value={editData.lastName}
                    onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-role">Role</Label>
                <Select 
                  value={editData.role} 
                  onValueChange={(value) => setEditData({ ...editData, role: value as UpdateUserInvitationRequest["role"] })}
                >
                  <SelectTrigger data-testid="select-edit-role">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user" data-testid="edit-role-option-user">User</SelectItem>
                    <SelectItem value="admin" data-testid="edit-role-option-admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setEditInviteDialogOpen(false);
                  setEditingInvitation(null);
                }}
                data-testid="button-cancel-edit-invite"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateInvitation} 
                disabled={editInvitationMutation.isPending}
                data-testid="button-update-invite"
              >
                {editInvitationMutation.isPending ? "Updating..." : "Update Invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" />
            Users ({users?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="invitations" data-testid="tab-invitations">
            <Mail className="w-4 h-4 mr-2" />
            Invitations ({activeInvitations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Users</CardTitle>
              <CardDescription>
                Current users with access to your organization
              </CardDescription>
              {isMasterAdmin && allTenants && allTenants.length > 0 && (
                <div className="mt-4">
                  <Label htmlFor="viewing-tenant-users">View Users For Tenant</Label>
                  <Select 
                    value={viewingTenantId || "current"} 
                    onValueChange={(value) => {
                      setViewingTenantId(value === "current" ? undefined : value);
                      setSelectedUserIds(new Set());
                    }}
                  >
                    <SelectTrigger id="viewing-tenant-users" data-testid="select-viewing-tenant-users" className="mt-2">
                      <SelectValue placeholder="Select tenant to view" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="current">Current Tenant</SelectItem>
                      {allTenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id} data-testid={`viewing-tenant-users-option-${tenant.id}`}>
                          {tenant.name} ({tenant.slug})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div data-testid="loading-users">Loading users...</div>
              ) : !users?.length ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="no-users">
                  No users found. Start by inviting team members.
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Bulk action bar */}
                  {users.length > 1 && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground select-none">
                        <Checkbox
                          checked={selectedUserIds.size === users.length}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedUserIds(new Set(users.map((u) => u.id)));
                            } else {
                              setSelectedUserIds(new Set());
                            }
                          }}
                          data-testid="checkbox-select-all-users"
                        />
                        {selectedUserIds.size > 0
                          ? `${selectedUserIds.size} selected`
                          : "Select all"}
                      </label>
                      {selectedUserIds.size > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => openBulkDeleteDialog(selectedUserIds)}
                          data-testid="button-bulk-delete-users"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove {selectedUserIds.size} user{selectedUserIds.size === 1 ? "" : "s"}
                        </Button>
                      )}
                    </div>
                  )}
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`user-${user.id}`}>
                      <div className="flex items-center space-x-4">
                        {users.length > 1 && (
                          <Checkbox
                            checked={selectedUserIds.has(user.id)}
                            onCheckedChange={(checked) => {
                              setSelectedUserIds((prev) => {
                                const next = new Set(prev);
                                if (checked) {
                                  next.add(user.id);
                                } else {
                                  next.delete(user.id);
                                }
                                return next;
                              });
                            }}
                            data-testid={`checkbox-user-${user.id}`}
                          />
                        )}
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.firstName?.[0] || user.email?.[0]?.toUpperCase() || "U"}
                        </div>
                        <div>
                          <Link href={`/user-profile/${user.id}`}>
                            <div className="font-medium hover:text-primary cursor-pointer transition-colors" data-testid={`user-name-${user.id}`}>
                              {user.firstName && user.lastName 
                                ? `${user.firstName} ${user.lastName}`
                                : user.email
                              }
                            </div>
                          </Link>
                          <div className="text-sm text-muted-foreground" data-testid={`user-email-${user.id}`}>
                            {user.email}
                          </div>
                          {user.tenantName && (
                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1" data-testid={`user-tenant-${user.id}`}>
                              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                              {user.tenantName}
                            </div>
                          )}
                          {isMasterAdmin && user.tenantId && (
                            <div className="mt-1">
                              <Badge variant="outline" className="text-xs gap-1 border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-400" data-testid={`user-plan-${user.id}`}>
                                <CreditCard className="w-3 h-3" />
                                {getPlanDisplayName(user.tenantId)}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {getRoleBadge(user.role, user.isMasterAdmin)}
                        <div className="text-sm text-muted-foreground" data-testid={`user-joined-${user.id}`}>
                          Joined {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "Unknown"}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setUserToChangeRole(user);
                            setNewRole(user.role === "admin" ? "user" : "admin");
                            setChangeRoleDialogOpen(true);
                          }}
                          data-testid={`button-change-role-${user.id}`}
                          title="Change User Role"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {user.username && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" data-testid={`button-reset-password-${user.id}`} title="Reset Password">
                                <KeyRound className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Reset Password</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to reset the password for {user.firstName ? `${user.firstName} ${user.lastName}` : user.email}?
                                  A temporary password will be sent to their email address.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid="button-cancel-reset">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => resetPasswordMutation.mutate(user.id)}
                                  disabled={resetPasswordMutation.isPending}
                                  data-testid="button-confirm-reset"
                                >
                                  Reset Password
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        {isMasterAdmin && user.tenantId && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const tenant = allTenants?.find(t => t.id === user.tenantId);
                              setTenantToChangePlan({
                                id: user.tenantId,
                                name: user.tenantName || "Unknown Org",
                                currentPlanId: tenant?.currentPlanId,
                              });
                              setSelectedPlanId(tenant?.currentPlanId || "");
                              setChangePlanDialogOpen(true);
                            }}
                            data-testid={`button-change-plan-${user.id}`}
                            title="Change Tenant Plan"
                          >
                            <CreditCard className="w-4 h-4" />
                          </Button>
                        )}
                        {isMasterAdmin && user.id !== currentUser?.id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`button-toggle-master-admin-${user.id}`}
                                title={user.isMasterAdmin ? "Remove Master Admin" : "Make Master Admin"}
                              >
                                <Crown className={`w-4 h-4 ${user.isMasterAdmin ? "text-amber-500" : ""}`} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {user.isMasterAdmin ? "Remove Master Admin?" : "Make Master Admin?"}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {user.isMasterAdmin
                                    ? `${user.firstName ? `${user.firstName} ${user.lastName}` : user.email} will lose master admin access, including cross-organization management and platform settings.`
                                    : `${user.firstName ? `${user.firstName} ${user.lastName}` : user.email} will get full master admin access — they can manage every organization, all users, billing, and platform settings.`}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid="button-cancel-master-admin">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => masterAdminMutation.mutate({ userId: user.id, grant: !user.isMasterAdmin })}
                                  disabled={masterAdminMutation.isPending}
                                  data-testid="button-confirm-master-admin"
                                >
                                  {user.isMasterAdmin ? "Remove Master Admin" : "Make Master Admin"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        {isMasterAdmin && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setUserToMove(user);
                              setMoveUserDialogOpen(true);
                            }}
                            data-testid={`button-move-user-${user.id}`}
                            title="Move User to Another Tenant"
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`button-delete-user-${user.id}`}
                          onClick={() => openDeleteDialog(user)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invitations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Invitations</CardTitle>
              <CardDescription>
                Invitations sent to new team members
              </CardDescription>
              {isMasterAdmin && allTenants && allTenants.length > 0 && (
                <div className="mt-4">
                  <Label htmlFor="viewing-tenant">View Invitations For Tenant</Label>
                  <Select 
                    value={viewingTenantId || "current"} 
                    onValueChange={(value) => setViewingTenantId(value === "current" ? undefined : value)}
                  >
                    <SelectTrigger id="viewing-tenant" data-testid="select-viewing-tenant" className="mt-2">
                      <SelectValue placeholder="Select tenant to view" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="current">Current Tenant</SelectItem>
                      {allTenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id} data-testid={`viewing-tenant-option-${tenant.id}`}>
                          {tenant.name} ({tenant.slug})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {invitationsLoading ? (
                <div data-testid="loading-invitations">Loading invitations...</div>
              ) : (
                <div className="space-y-4">
                  {staleInvitations.length > 0 && (
                    <div className="flex justify-end gap-2">
                      {showAllInvitations && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setClearStaleDialogOpen(true)}
                          disabled={clearStaleInvitationsMutation.isPending}
                          data-testid="button-clear-stale-invitations"
                          className="gap-1.5 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                          Clear expired & cancelled ({staleInvitations.length})
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAllInvitations((v) => !v)}
                        data-testid="button-toggle-show-all-invitations"
                      >
                        {showAllInvitations
                          ? "Hide expired & cancelled"
                          : `Show all (${staleInvitations.length} expired/cancelled)`}
                      </Button>
                    </div>
                  )}
                  {visibleInvitations.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground" data-testid="no-invitations">
                      No pending invitations. Use the "Invite User" button to add team members. Accepted users appear in the Users tab.
                    </div>
                  ) : (
                  visibleInvitations.map((invitation) => {
                    const sentAt = invitation.createdAt ? new Date(invitation.createdAt) : null;
                    const lastSentAt = invitation.lastSentAt ? new Date(invitation.lastSentAt) : null;
                    const displaySent = lastSentAt || sentAt;
                    const wasResent = lastSentAt && sentAt && lastSentAt.getTime() - sentAt.getTime() > 60000;
                    // Client-side expiry: if a pending invite's expiresAt has passed while the
                    // page is open, render it as expired until the next refetch confirms.
                    const expiresAtDate = invitation.expiresAt ? new Date(invitation.expiresAt) : null;
                    const lapsedClientSide =
                      invitation.status === "pending" &&
                      expiresAtDate !== null &&
                      !isNaN(expiresAtDate.getTime()) &&
                      expiresAtDate.getTime() <= now;
                    const effectiveStatus = lapsedClientSide ? "expired" : invitation.status;
                    const expiryInfo = effectiveStatus === "pending" ? getExpiryInfo(invitation.expiresAt) : null;
                    return (
                    <div key={invitation.id} className={`flex items-start justify-between p-4 border rounded-lg gap-4 ${effectiveStatus === "expired" ? "opacity-70" : expiryInfo?.urgent ? "border-amber-400 dark:border-amber-600 bg-amber-50/50 dark:bg-amber-950/20" : ""}`} data-testid={`invitation-${invitation.id}`}>
                      <div className="flex items-start space-x-4 min-w-0">
                        <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white font-semibold shrink-0">
                          {invitation.firstName?.[0] || invitation.email[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium" data-testid={`invitation-name-${invitation.id}`}>
                            {invitation.firstName && invitation.lastName 
                              ? `${invitation.firstName} ${invitation.lastName}`
                              : invitation.email
                            }
                          </div>
                          <div className="text-sm text-muted-foreground" data-testid={`invitation-email-${invitation.id}`}>
                            {invitation.email}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                            {sentAt && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`invitation-sent-${invitation.id}`}>
                                <Clock className="w-3 h-3" />
                                {wasResent ? "Originally sent" : "Invited"} {sentAt.toLocaleDateString()} at {sentAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                            {wasResent && lastSentAt && (
                              <div className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400" data-testid={`invitation-resent-${invitation.id}`}>
                                <RotateCw className="w-3 h-3" />
                                Last resent {lastSentAt.toLocaleDateString()} at {lastSentAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                            {expiryInfo && (
                              <div
                                className={`flex items-center gap-1 text-xs ${expiryInfo.urgent ? "font-semibold text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}
                                data-testid={`invitation-expiry-${invitation.id}`}
                              >
                                <Clock className="w-3 h-3" />
                                {expiryInfo.label}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                        {getRoleBadge(invitation.role)}
                        {getStatusBadge(effectiveStatus)}
                        <div className="flex gap-2">
                          {effectiveStatus === "pending" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditInvitation(invitation)}
                              data-testid={`button-edit-invitation-${invitation.id}`}
                              title="Edit invitation"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {(effectiveStatus === "pending" || effectiveStatus === "expired") && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => resendInvitationMutation.mutate(invitation.id)}
                                disabled={resendInvitationMutation.isPending && resendInvitationMutation.variables === invitation.id}
                                data-testid={`button-resend-invitation-${invitation.id}`}
                                className="gap-1.5"
                              >
                                {resendInvitationMutation.isPending && resendInvitationMutation.variables === invitation.id
                                  ? <RotateCw className="w-4 h-4 animate-spin" />
                                  : <Send className="w-4 h-4" />}
                                Resend
                              </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openRevokeInvitationDialog(invitation)}
                            data-testid={`button-delete-invitation-${invitation.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                  })
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete User Dialog — controlled so we can show a deletion-preview warning */}
      <AlertDialog
        open={!!deleteDialogUser}
        onOpenChange={(open) => {
          if (!open && !deleteUserMutation.isPending) {
            setDeleteDialogUser(null);
            setDeletionPreview(null);
          }
        }}
      >
        <AlertDialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Are you sure you want to remove{" "}
                  {deleteDialogUser?.firstName
                    ? `${deleteDialogUser.firstName} ${deleteDialogUser.lastName}`
                    : deleteDialogUser?.email}{" "}
                  from your organization? This action cannot be undone.
                </p>
                {deletionPreviewLoading && (
                  <p className="text-sm text-muted-foreground">Checking for data that will be permanently lost…</p>
                )}
                {!deletionPreviewLoading && deletionPreview?.permanentLoss && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive space-y-1">
                    <p className="font-semibold">⚠ Permanent data loss</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {deletionPreview.warnings.map((warning, i) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                    {deletionPreview.lastConversationAt && (
                      <p className="text-xs" data-testid="text-last-conversation-delete">
                        Last AI conversation: {formatDaysAgo(deletionPreview.lastConversationAt)}
                      </p>
                    )}
                    <p className="text-xs text-destructive/80">This cannot be undone.</p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete" disabled={deleteUserMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (!deleteDialogUser || deletionPreviewLoading || deleteUserPendingRef.current) return;
                deleteUserPendingRef.current = true;
                deleteUserMutation.mutate(deleteDialogUser.id);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
              disabled={deleteUserMutation.isPending || deletionPreviewLoading}
            >
              Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Invitation Dialog — controlled so we can show a deletion-preview warning for accepted invitations */}
      <AlertDialog
        open={!!revokeDialogInvitation}
        onOpenChange={(open) => {
          if (!open) {
            setRevokeDialogInvitation(null);
            setRevokeDialogPreview(null);
          }
        }}
      >
        <AlertDialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Are you sure you want to cancel the invitation for{" "}
                  {revokeDialogInvitation?.firstName && revokeDialogInvitation?.lastName
                    ? `${revokeDialogInvitation.firstName} ${revokeDialogInvitation.lastName} (${revokeDialogInvitation.email})`
                    : revokeDialogInvitation?.email}?
                  This action cannot be undone.
                </p>
                {revokeDialogPreviewLoading && (
                  <p className="text-sm text-muted-foreground">Checking for data that will be permanently lost…</p>
                )}
                {!revokeDialogPreviewLoading && revokeDialogPreviewError && (
                  <p className="text-sm text-muted-foreground" data-testid="revoke-preview-error">
                    Could not load impact details — proceed carefully as data loss may occur.
                  </p>
                )}
                {!revokeDialogPreviewLoading && !revokeDialogPreviewError && revokeDialogPreview?.permanentLoss && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive space-y-1">
                    <p className="font-semibold">⚠ Permanent data loss</p>
                    <p className="text-muted-foreground text-xs">
                      This invitation has already been accepted. Cancelling it will also delete the user's account.
                    </p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {revokeDialogPreview.warnings.map((warning, i) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                    {revokeDialogPreview.lastConversationAt && (
                      <p className="text-xs" data-testid="text-last-conversation-revoke">
                        Last AI conversation: {formatDaysAgo(revokeDialogPreview.lastConversationAt)}
                      </p>
                    )}
                    <p className="text-xs text-destructive/80">This cannot be undone.</p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-invitation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                revokeDialogInvitation &&
                deleteInvitationMutation.mutate({
                  id: revokeDialogInvitation.id,
                  force: revokeDialogPreview?.permanentLoss === true,
                })
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-invitation"
              disabled={deleteInvitationMutation.isPending || revokeDialogPreviewLoading}
            >
              {deleteInvitationMutation.isPending ? "Cancelling…" : "Cancel Invitation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Expired & Cancelled Invitations Dialog */}
      <AlertDialog open={clearStaleDialogOpen} onOpenChange={setClearStaleDialogOpen}>
        <AlertDialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Expired & Cancelled Invitations</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete {staleInvitations.length} expired/cancelled invitation{staleInvitations.length === 1 ? "" : "s"}? Pending and accepted invitations will not be affected. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-clear-stale">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => clearStaleInvitationsMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-clear-stale"
              disabled={clearStaleInvitationsMutation.isPending}
            >
              {clearStaleInvitationsMutation.isPending ? "Clearing…" : "Delete All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Remove Users Dialog */}
      <AlertDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={(open) => {
          if (!open && !bulkDeleteUserMutation.isPending) {
            setBulkDeleteDialogOpen(false);
            setBulkDeletionPreview(null);
          }
        }}
      >
        <AlertDialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {selectedUserIds.size} User{selectedUserIds.size === 1 ? "" : "s"}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Are you sure you want to remove {selectedUserIds.size} user{selectedUserIds.size === 1 ? "" : "s"} from your organization? This action cannot be undone.
                </p>
                {bulkDeletionPreviewLoading && (
                  <p className="text-sm text-muted-foreground">Checking for data that will be permanently lost…</p>
                )}
                {!bulkDeletionPreviewLoading && bulkDeletionPreview?.permanentLoss && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive space-y-1">
                    <p className="font-semibold">⚠ Permanent data loss</p>
                    <p>
                      <span className="font-medium">
                        {bulkDeletionPreview.totalConversationCount} AI conversation{bulkDeletionPreview.totalConversationCount === 1 ? "" : "s"}
                      </span>
                      {" across these users"}
                      {bulkDeletionPreview.mostRecentLastActiveAt && (
                        <span className="text-muted-foreground">
                          {", most recently active "}
                          {formatDaysAgo(bulkDeletionPreview.mostRecentLastActiveAt)}
                        </span>
                      )}
                      {" will be permanently deleted along with all their messages. This cannot be undone."}
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-bulk-delete" disabled={bulkDeleteUserMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (bulkDeletePendingRef.current || bulkDeletionPreviewLoading || selectedUserIds.size === 0) return;
                bulkDeletePendingRef.current = true;
                bulkDeleteUserMutation.mutate(Array.from(selectedUserIds));
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-bulk-delete"
              disabled={bulkDeleteUserMutation.isPending || bulkDeletionPreviewLoading}
            >
              {bulkDeleteUserMutation.isPending ? "Removing…" : `Remove ${selectedUserIds.size} User${selectedUserIds.size === 1 ? "" : "s"}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move User Dialog */}
      <Dialog open={moveUserDialogOpen} onOpenChange={setMoveUserDialogOpen}>
        <DialogContent data-testid="dialog-move-user" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Move User to Another Tenant</DialogTitle>
            <DialogDescription>
              Select the target tenant to move {userToMove?.firstName ? `${userToMove.firstName} ${userToMove.lastName}` : userToMove?.email} to.
              All of their data will be migrated to the new tenant.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="target-tenant">Target Tenant</Label>
              <Select value={targetTenantId} onValueChange={setTargetTenantId}>
                <SelectTrigger id="target-tenant" data-testid="select-target-tenant">
                  <SelectValue placeholder="Select a tenant" />
                </SelectTrigger>
                <SelectContent>
                  {allTenants?.filter(t => t.id !== userToMove?.tenantId).map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id} data-testid={`tenant-option-${tenant.id}`}>
                      {tenant.name} ({tenant.slug})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {userToMove && (
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <p className="text-sm text-muted-foreground">
                  <strong>Current Tenant:</strong> {userToMove.tenantName || "Unknown"}
                </p>
                <div className="border-t pt-3">
                  <p className="text-sm font-medium mb-2">What will be migrated:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>All goals, milestones, and tasks</li>
                    <li>All habits and habit tracking data</li>
                    <li>All journal entries and templates</li>
                    <li>All notes and checklists</li>
                    <li>Calendar connections and events</li>
                    <li>Dashboard settings and preferences</li>
                    <li>Categories and all other user data</li>
                  </ul>
                </div>
                <div className="border-t pt-3">
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    <strong>⚠️ Important:</strong> This operation may take a few moments if the user has a lot of data. 
                    The migration is atomic - all data will be moved together or the operation will be rolled back if any errors occur.
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setMoveUserDialogOpen(false);
                setUserToMove(null);
                setTargetTenantId("");
              }}
              data-testid="button-cancel-move"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (userToMove && targetTenantId) {
                  moveUserMutation.mutate({
                    userId: userToMove.id,
                    targetTenantId,
                  });
                }
              }}
              disabled={!targetTenantId || moveUserMutation.isPending}
              data-testid="button-confirm-move"
            >
              {moveUserMutation.isPending ? "Moving..." : "Move User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog (master admin only) */}
      <Dialog open={changePlanDialogOpen} onOpenChange={setChangePlanDialogOpen}>
        <DialogContent data-testid="dialog-change-plan" >
          <DialogHeader>
            <DialogTitle>Change Subscription Plan</DialogTitle>
            <DialogDescription>
              Change the plan for <strong>{tenantToChangePlan?.name}</strong>. This takes effect immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-plan">Select Plan</Label>
              <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                <SelectTrigger id="new-plan" data-testid="select-new-plan">
                  <SelectValue placeholder="Choose a plan..." />
                </SelectTrigger>
                <SelectContent>
                  {resellerPlans && resellerPlans.length > 0 ? (
                    resellerPlans.map(plan => (
                      <SelectItem key={plan.id} value={plan.id} data-testid={`plan-option-${plan.id}`}>
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          {plan.name}
                          {!plan.isActive && <span className="text-xs text-muted-foreground">(inactive)</span>}
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>No plans configured yet</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {tenantToChangePlan && (
              <div className="p-4 bg-muted rounded-lg space-y-1 text-sm text-muted-foreground">
                <p><strong>Organization:</strong> {tenantToChangePlan.name}</p>
                <p><strong>Current Plan:</strong> {getPlanDisplayName(tenantToChangePlan.id)}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setChangePlanDialogOpen(false);
                setTenantToChangePlan(null);
                setSelectedPlanId("");
              }}
              data-testid="button-cancel-change-plan"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (tenantToChangePlan && selectedPlanId) {
                  changePlanMutation.mutate({ tenantId: tenantToChangePlan.id, planId: selectedPlanId });
                }
              }}
              disabled={!selectedPlanId || selectedPlanId === "none" || changePlanMutation.isPending}
              data-testid="button-confirm-change-plan"
            >
              {changePlanMutation.isPending ? "Updating..." : "Change Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={changeRoleDialogOpen} onOpenChange={setChangeRoleDialogOpen}>
        <DialogContent data-testid="dialog-change-role" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Change the role for {userToChangeRole?.firstName ? `${userToChangeRole.firstName} ${userToChangeRole.lastName}` : userToChangeRole?.email}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-role">New Role</Label>
              <Select value={newRole} onValueChange={(value: "user" | "admin") => setNewRole(value)}>
                <SelectTrigger id="new-role" data-testid="select-new-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user" data-testid="role-option-user">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      User
                    </div>
                  </SelectItem>
                  <SelectItem value="admin" data-testid="role-option-admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Admin
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {userToChangeRole && (
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p className="text-sm text-muted-foreground">
                  <strong>Current Role:</strong> {userToChangeRole.role === "admin" ? "Admin" : "User"}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>New Role:</strong> {newRole === "admin" ? "Admin" : "User"}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setChangeRoleDialogOpen(false);
                setUserToChangeRole(null);
              }}
              data-testid="button-cancel-change-role"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (userToChangeRole && !changeRolePendingRef.current) {
                  changeRolePendingRef.current = true;
                  changeRoleMutation.mutate({
                    userId: userToChangeRole.id,
                    role: newRole,
                  });
                }
              }}
              disabled={changeRoleMutation.isPending}
              data-testid="button-confirm-change-role"
            >
              {changeRoleMutation.isPending ? "Updating..." : "Change Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type InviteUserData = CreateUserInvitationRequest;
